<template>
  <!-- ============ 各组事务（待办 / 可撤销日志 / 奖励队列 三合一） ============ -->
  <v-card class="my-4">
    <div class="d-flex justify-space-between align-center px-4 py-2 bg-indigo-lighten-2">
      <span class="text-h6">各组事务</span>
      <div class="d-flex" style="gap: 8px">
        <v-btn
          size="small"
          color="success"
          @click="openRewardModal"
          v-if="GameStatus && GameStatus.chessPhase === 3"
        >
          <v-icon icon="mdi-gift" class="mr-1"></v-icon>
          优秀者奖励
        </v-btn>
        <v-btn
          size="small"
          color="warning"
          @click="confirmNextRound"
          v-if="GameStatus && GameStatus.chessPhase === 3 && (eventData?.length ?? 0) === 0"
        >
          <v-icon icon="mdi-arrow-right-bold" class="mr-1"></v-icon>
          进入下一轮
        </v-btn>
      </div>
    </div>

    <v-dialog v-model="nextRoundConfirm" max-width="420">
      <v-card>
        <v-card-title class="text-h6">确认进入下一轮？</v-card-title>
        <v-card-text>
          进入下一轮后，本轮所有事件将不可撤销。<br />
          <span class="text-caption text-grey">请确认所有结算无误后再继续。</span>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn color="grey" variant="text" @click="nextRoundConfirm = false">取消</v-btn>
          <v-btn color="warning" variant="elevated" @click="doNextRound">确认进入</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-tabs v-model="activeTab" color="primary" grow density="comfortable">
      <v-tab value="todo">
        <v-icon icon="mdi-clipboard-list" class="mr-1"></v-icon>
        待办事项
      </v-tab>
      <v-tab value="log" v-if="GameStatus && GameStatus.stage === 1">
        <v-icon icon="mdi-history" class="mr-1"></v-icon>
        可撤销日志
        <v-chip
          v-if="revocableCount > 0"
          size="x-small"
          color="orange"
          class="ml-2"
        >{{ revocableCount }}</v-chip>
      </v-tab>
    </v-tabs>

    <v-window v-model="activeTab">
      <!-- 待办事项（合并：未结算特殊格 + 暂存奖励） -->
      <v-window-item value="todo">
        <div v-if="!eventData" class="d-flex justify-center my-12 text-h6 text-grey">结算阶段展示</div>
        <div v-else-if="todoList.length === 0" class="d-flex justify-center my-12 text-h6 text-grey">暂无待办事项</div>
        <v-list v-else class="pa-2 overflow-y-auto" max-height="260px">
          <v-list-item
            class="d-flex"
            v-for="row of todoList"
            :key="row.teamId"
          >
            <span class="mr-2 font-weight-medium">第 {{ row.teamId }} 组：</span>
            <!-- 原有特殊格事件 chip -->
            <v-chip
              v-for="(e, subIdx) of row.tiles"
              :color="e.tileType === 1 ? 'orange' : 'blue'"
              :key="`tile-${subIdx}`"
              class="mr-2 mb-1"
              @click="handleClickTile(row.teamId, e)"
            >
              {{ e.eventName }}
            </v-chip>
            <!-- 暂存的优秀者奖励 chip：追加到该组待办尾部，点击弹出"发放/编辑/删除"菜单 -->
            <v-menu v-for="r of row.rewards" :key="`reward-${r.id}`">
              <template v-slot:activator="{ props: menuProps }">
                <v-chip
                  v-bind="menuProps"
                  color="success"
                  variant="elevated"
                  class="mr-2 mb-1"
                >
                  <v-icon icon="mdi-gift" start></v-icon>
                  优秀者 +{{ r.stepCount }} 步
                  <span v-if="r.reason" class="ml-1 text-caption">（{{ r.reason }}）</span>
                </v-chip>
              </template>
              <v-list density="compact">
                <v-list-item @click="openIssueRewardModal(r)">
                  <template v-slot:prepend>
                    <v-icon icon="mdi-send" color="success"></v-icon>
                  </template>
                  <v-list-item-title>发放（选择落点格子）</v-list-item-title>
                </v-list-item>
                <v-list-item @click="openEditRewardModal(r)">
                  <template v-slot:prepend>
                    <v-icon icon="mdi-pencil" color="orange"></v-icon>
                  </template>
                  <v-list-item-title>编辑步数 / 原因</v-list-item-title>
                </v-list-item>
                <v-list-item @click="deleteRewardItem(r.id)">
                  <template v-slot:prepend>
                    <v-icon icon="mdi-delete" color="error"></v-icon>
                  </template>
                  <v-list-item-title>删除</v-list-item-title>
                </v-list-item>
              </v-list>
            </v-menu>
          </v-list-item>
        </v-list>
      </v-window-item>

      <!-- 可撤销日志 -->
      <v-window-item value="log">
        <div class="d-flex justify-end px-4 pt-2">
          <v-btn size="small" color="primary" variant="tonal" @click="loadTeamLogs">
            <v-icon icon="mdi-refresh" class="mr-1"></v-icon>
            刷新
          </v-btn>
        </div>
        <div v-if="!teamLogs" class="d-flex justify-center my-8 text-h6 text-grey">加载中...</div>
        <div
          v-else-if="!teamLogs.list || teamLogs.list.length === 0"
          class="d-flex justify-center my-8 text-h6 text-grey"
        >
          暂无事件记录
        </div>
        <v-list v-else class="pa-2 overflow-y-auto" max-height="280px">
          <v-list-item
            v-for="logItem in teamLogs.list"
            :key="logItem.id"
            :class="{ 'opacity-50': logItem.comment === 'REVOKED' }"
            class="mb-1"
          >
            <v-card variant="outlined" class="pa-2 w-100" :color="logItem.comment === 'REVOKED' ? 'grey' : 'default'">
              <div class="d-flex justify-space-between align-center">
                <div class="flex-1">
                  <!-- 事件标题 -->
                  <div class="font-weight-medium text-body-2">
                    {{ parseEventTitle(logItem.comment) }}
                  </div>
                  <!-- 事件详情：各组胜负 -->
                  <div class="mt-1">
                    <v-chip
                      v-for="(chip, idx) in parseEventChips(logItem.comment)"
                      :key="idx"
                      :color="chip.color"
                      size="x-small"
                      class="mr-1 mb-1"
                      variant="tonal"
                    >
                      {{ chip.label }}
                    </v-chip>
                  </div>
                  <div v-if="logItem.comment === 'REVOKED'" class="text-red text-caption mt-1">已撤销，格子已还原</div>
                </div>
                <v-btn
                  v-if="logItem.comment !== 'REVOKED'"
                  size="small"
                  color="error"
                  variant="tonal"
                  class="ml-2 flex-shrink-0"
                  @click="confirmRevoke(logItem.id)"
                >
                  <v-icon icon="mdi-undo" class="mr-1"></v-icon>
                  撤销
                </v-btn>
              </div>
            </v-card>
          </v-list-item>
        </v-list>
      </v-window-item>

    </v-window>
  </v-card>

  <!-- ============ 事件结算表单 overlay ============ -->
  <v-overlay v-model="submitObject.showOverlay" class="align-center justify-center">
    <DynamicForm
      v-if="submitObject.type === 1"
      :headers="['小组号', '参加队伍二', '参加队伍三', '获胜方编号', 'ID', '事件类型']"
      :excluded-keys="['Type', 'tileId']"
      :model="submitObject.data"
      @submit="useSubmitBlindBox"
      @close="submitObject.showOverlay = false"
    />
    <DynamicForm
      v-if="submitObject.type === 2"
      :headers="['进攻方', '防守方', '获胜方编号', '事件类型', 'ID']"
      :excluded-keys="['Type', 'tileId']"
      :model="submitObject.data"
      @submit="useSubmitFortress"
      @close="submitObject.showOverlay = false"
    />
    <DynamicForm
      v-if="submitObject.type === 3"
      :headers="['进攻方', '防守方', '获胜方编号', '事件类型', 'ID']"
      :excluded-keys="['Type', 'tileId']"
      :model="submitObject.data"
      @submit="useSubmitFortress"
      @close="submitObject.showOverlay = false"
    />
  </v-overlay>

  <!-- ============ 新增优秀者奖励：用 DynamicForm 保持和事件结算同款样式 ============ -->
  <v-overlay v-model="rewardObject.showOverlay" class="align-center justify-center">
    <DynamicForm
      title="请填写优秀者奖励"
      :headers="['获奖小组号', '奖励步数', '奖励原因']"
      :model="rewardObject.data"
      @submit="useSubmitReward"
      @close="rewardObject.showOverlay = false"
    />
  </v-overlay>

  <!-- ============ 编辑暂存奖励：同样使用 DynamicForm（仅可改步数/原因，不可换组） ============ -->
  <v-overlay v-model="editRewardObject.showOverlay" class="align-center justify-center">
    <DynamicForm
      title="编辑暂存奖励"
      :headers="['奖励步数', '奖励原因']"
      :model="editRewardObject.data"
      @submit="useSubmitEditReward"
      @close="editRewardObject.showOverlay = false"
    />
  </v-overlay>

  <!-- ============ 撤销确认弹窗 ============ -->
  <v-dialog v-model="revokeConfirm.show" max-width="420px">
    <v-card>
      <v-card-title class="text-h6">确认撤销该事件结算？</v-card-title>
      <v-card-text>
        撤销后将回滚相关小组的领地与得分状态，请确认无误。<br />
        <span class="text-caption text-grey">注意：仅当后续没有依赖该结算的新事件时才可撤销。</span>
      </v-card-text>
      <v-card-actions>
        <v-spacer />
        <v-btn color="grey" variant="text" @click="revokeConfirm.show = false">取消</v-btn>
        <v-btn color="error" variant="elevated" @click="doRevoke">确认撤销</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script setup lang="ts">
import { defineProps, defineEmits, onMounted, reactive, ref, watch, computed } from 'vue'
import { useApi } from '@/api/handler'
import { ApiMap } from '@/api/type'
import { game, log } from '@/api'
import DynamicForm from '@/components/DynamicForm.vue'

const props = defineProps<{
  data?: ApiMap['/game/status/:id']['resp']
}>()

const emits = defineEmits<{
  (e: 'update'): void
  (e: 'startIssueReward', payload: { rewardId: string; teamId: number; stepCount: number }): void
}>()

const GameStatus = ref<ApiMap['/game/status/:id']['resp'] | null>(null)
const eventData = ref<ApiMap['/game/special/list']['resp'] | null>(null)
const rewardQueue = ref<ApiMap['/game/reward/queue']['resp'] | null>(null)
const teamLogs = ref<ApiMap['/log/team/list']['resp'] | null>(null)

const activeTab = ref<'todo' | 'log'>('todo')

// 事件结算表单
const submitObject = reactive<{
  type: number
  showOverlay: boolean
  data: Record<string, unknown>
}>({
  type: 0,
  showOverlay: false,
  data: {}
})

// 新增优秀者奖励：DynamicForm 数据模型（与事件结算同款）
const rewardObject = reactive<{
  showOverlay: boolean
  data: Record<string, unknown>
}>({
  showOverlay: false,
  data: {}
})

// 编辑暂存奖励：DynamicForm 数据模型；id 单独保存（不放进 model，避免被 excluded-keys 字符串匹配误伤）
const editingRewardId = ref<string>('')
const editRewardObject = reactive<{
  showOverlay: boolean
  data: Record<string, unknown>
}>({
  showOverlay: false,
  data: {}
})

const issuingReward = ref<{ id: number; stepCount: number; teamId: number } | null>(null)

// 进入下一轮确认弹窗
const nextRoundConfirm = ref(false)
const confirmNextRound = () => { nextRoundConfirm.value = true }
const doNextRound = () => {
  if (!GameStatus.value) return
  useApi({
    api: game.NextRound(GameStatus.value.id),
    onSuccess: () => {
      nextRoundConfirm.value = false
      emits('update')
    },
    tip: '已进入下一轮'
  })
}

// 撤销二次确认
const revokeConfirm = reactive({
  show: false,
  logId: 0
})

// 可撤销条目数量（用于 tab 上的角标）
const revocableCount = computed(() => {
  if (!teamLogs.value || !teamLogs.value.list) return 0
  return teamLogs.value.list.filter((l) => l.comment !== 'REVOKED' && l.comment !== null).length
})

// 待办事项合并视图：把暂存的优秀者奖励按 teamId 合并到对应组的待办行尾部
type TodoTile = ApiMap['/game/special/list']['resp'][number]['unTriggeredTiles'][number]
type TodoReward = ApiMap['/game/reward/queue']['resp']['rewards'][number]
const todoList = computed<Array<{ teamId: number; tiles: TodoTile[]; rewards: TodoReward[] }>>(() => {
  const map = new Map<number, { teamId: number; tiles: TodoTile[]; rewards: TodoReward[] }>()
  if (eventData.value) {
    for (const t of eventData.value) {
      map.set(t.teamId, {
        teamId: t.teamId,
        tiles: t.unTriggeredTiles ?? [],
        rewards: []
      })
    }
  }
  if (rewardQueue.value && rewardQueue.value.rewards) {
    for (const r of rewardQueue.value.rewards) {
      let entry = map.get(r.teamId)
      if (!entry) {
        entry = { teamId: r.teamId, tiles: [], rewards: [] }
        map.set(r.teamId, entry)
      }
      entry.rewards.push(r)
    }
  }
  // 有待办格子事件的组排在前面，纯奖励的组排在末尾，组内按组号升序
  return Array.from(map.values()).sort((a, b) => {
    const aHasTile = a.tiles.length > 0 ? 0 : 1
    const bHasTile = b.tiles.length > 0 ? 0 : 1
    if (aHasTile !== bHasTile) return aHasTile - bHasTile
    return a.teamId - b.teamId
  })
})

// ---------- 撤销日志解析 ----------
function parseEventMeta(comment: string | null): Record<string, any> | null {
  if (!comment || comment === 'REVOKED') return null
  try {
    const data = JSON.parse(comment)
    if (Array.isArray(data)) {
      // 旧格式：纯快照数组，从中提取参与组号
      return { oldFormat: true, teamIds: data.map((t: any) => t.teamId) }
    }
    // 新格式：含 snap + 元数据的对象
    return data.snap !== undefined ? data : null
  } catch {
    return null
  }
}

function parseEventTitle(comment: string | null): string {
  const meta = parseEventMeta(comment)
  if (!meta) return '事件结算'
  if (meta.oldFormat) {
    const teams = (meta.teamIds as number[]).map(id => `第${id}组`).join(' VS ')
    return `事件结算：${teams}`
  }
  if (meta.eventType === 11) {
    return `决斗要塞：第${meta.attackerId}组(攻) VS 第${meta.defenderId}组(守)`
  }
  if (meta.eventType === 10) {
    const allIds = meta.winnerId
      ? [meta.winnerId, ...((meta.loserIds as number[]) ?? [])]
      : ((meta.loserIds as number[]) ?? [])
    const teams = allIds.map(id => `第${id}组`).join(' VS ')
    return `盲盒秘境：${teams}`
  }
  // 奖励发放（eventType=12）已废弃显示，业务上不可撤销
  return '事件结算'
}

function parseEventChips(comment: string | null): Array<{ label: string; color: string }> {
  const meta = parseEventMeta(comment)
  if (!meta) return []

  if (meta.oldFormat) {
    return (meta.teamIds as number[]).map((id: number) => ({
      label: `第${id}组 参与`,
      color: 'primary'
    }))
  }

  const chips: Array<{ label: string; color: string }> = []

  if (meta.eventType === 11) {
    const winGain = meta.tileChange ?? 0
    if (meta.winnerId) chips.push({ label: `第${meta.winnerId}组 胜 +${winGain}格`, color: 'success' })
    if (meta.loserId) chips.push({ label: `第${meta.loserId}组 败 -${winGain}格`, color: 'error' })
  } else if (meta.eventType === 10) {
    if (meta.winnerId) chips.push({ label: `第${meta.winnerId}组 胜 +${meta.winnerGain ?? 0}格`, color: 'success' })
    const loserIds: number[] = meta.loserIds ?? []
    const loserChange: Record<string, number> = meta.loserTileChange ?? {}
    loserIds.forEach((lid: number) => {
      chips.push({ label: `第${lid}组 败 -${loserChange[String(lid)] ?? 0}格`, color: 'error' })
    })
  }
  return chips
}

// ---------- 生命周期 ----------
watch(() => props.data, (newVal) => {
  GameStatus.value = newVal ?? null
  useSpecialList()
  loadRewardQueue()
  loadTeamLogs()
})

onMounted(() => {
  if (props.data) GameStatus.value = props.data
  useSpecialList()
  loadRewardQueue()
  loadTeamLogs()
})

// 阶段从非结算切回结算外时，自动回到待办 tab
watch(
  () => GameStatus.value?.chessPhase,
  (phase) => {
    if (phase !== 3 && activeTab.value !== 'todo') {
      activeTab.value = 'todo'
    }
  }
)

// ---------- 待办事项 / 事件结算 ----------
const useSpecialList = () => {
  if (!GameStatus.value) return
  if (GameStatus.value.chessPhase === 3) {
    // 结算阶段：调用原接口（会在事件全清空时自动推进轮次）
    useApi({
      api: game.SpecialList(GameStatus.value.id),
      onSuccess: (resp) => {
        eventData.value = resp.data as ApiMap['/game/special/list']['resp']
      }
    })
  } else {
    // 非结算阶段：只读 preview，不触发轮次推进，用于显示奖励触发的特殊格子
    useApi({
      api: game.SpecialListPreview(GameStatus.value.id),
      onSuccess: (resp) => {
        eventData.value = resp.data as ApiMap['/game/special/list']['resp']
      }
    })
  }
}

function handleClick(index: number, subIndex: number) {
  if (!eventData.value) return
  const tile = eventData.value[index].unTriggeredTiles[subIndex]
  submitObject.type = tile.tileType
  let v_data: Record<string, unknown> | null = null
  if (tile.tileType === 1) {
    v_data = {
      teamId: eventData.value[index].teamId,
      involvedTeamOne: 0,
      involvedTeamTwo: 0,
      winnerTeamId: 0,
      tileId: tile.tileId,
      eventType: tile.eventType
    }
  } else if (tile.tileType === 2 || tile.tileType === 3) {
    v_data = {
      attackerTeamId: eventData.value[index].teamId,
      defenderTeamId: 0,
      winnerTeamId: 0,
      gameType: tile.eventType,
      tileId: tile.tileId
    }
  }
  if (!v_data) return
  submitObject.data = v_data
  submitObject.showOverlay = true
}

// 待办合并视图直接拿 (teamId, tile) 触发结算表单，等价于旧 handleClick
function handleClickTile(teamId: number, tile: TodoTile) {
  submitObject.type = tile.tileType
  let v_data: Record<string, unknown> | null = null
  if (tile.tileType === 1) {
    v_data = {
      teamId,
      involvedTeamOne: 0,
      involvedTeamTwo: 0,
      winnerTeamId: 0,
      tileId: tile.tileId,
      eventType: tile.eventType
    }
  } else if (tile.tileType === 2 || tile.tileType === 3) {
    v_data = {
      attackerTeamId: teamId,
      defenderTeamId: 0,
      winnerTeamId: 0,
      gameType: tile.eventType,
      tileId: tile.tileId
    }
  }
  if (!v_data) return
  submitObject.data = v_data
  submitObject.showOverlay = true
}

const useSubmitBlindBox = (data: unknown) => {
  if (!GameStatus.value) return
  const transData = data as {
    teamId: number
    involvedTeamOne: number
    involvedTeamTwo: number
    winnerTeamId: number
    tileId: number
    eventType: number
  }
  useApi({
    api: game.SubmitBlindBox({
      teamId: transData.teamId,
      winnerTeamId: Number(transData.winnerTeamId),
      eventType: transData.eventType,
      tileId: transData.tileId,
      gameId: GameStatus.value.id,
      involvedTeamIds: [
        Number(transData.teamId),
        Number(transData.involvedTeamOne),
        Number(transData.involvedTeamTwo)
      ]
    }),
    onSuccess: () => {
      emits('update')
      submitObject.showOverlay = false
      loadTeamLogs()
    },
    tip: '上传成功'
  })
}

const useSubmitFortress = (data: unknown) => {
  if (!GameStatus.value) return
  const transData = data as {
    attackerTeamId: number
    defenderTeamId: number
    winnerTeamId: number
    gameType: number
    tileId: number
  }
  useApi({
    api: game.SubmitFortress({
      attackerTeamId: Number(transData.attackerTeamId),
      defenderTeamId: Number(transData.defenderTeamId),
      winnerTeamId: Number(transData.winnerTeamId),
      gameType: transData.gameType,
      tileId: transData.tileId,
      gameId: GameStatus.value.id
    }),
    onSuccess: () => {
      emits('update')
      loadTeamLogs()
    },
    Finally: () => {
      submitObject.showOverlay = false
    },
    tip: '上传成功'
  })
}

// ---------- 撤销 ----------
const confirmRevoke = (logId: number) => {
  revokeConfirm.logId = logId
  revokeConfirm.show = true
}

const doRevoke = () => {
  if (!GameStatus.value || !revokeConfirm.logId) return
  useApi({
    api: game.RevokeEvent({
      gameId: GameStatus.value.id,
      logId: revokeConfirm.logId
    }),
    onSuccess: () => {
      revokeConfirm.show = false
      revokeConfirm.logId = 0
      loadTeamLogs()
      useSpecialList()
      emits('update')
    },
    tip: '撤销成功，棋盘状态已回滚'
  })
}

// ---------- 事件日志 ----------
const loadTeamLogs = () => {
  if (!GameStatus.value) return
  useApi({
    api: log.TeamLog({
      gameId: GameStatus.value.id,
      teamId: 0,
      pageNum: 1,
      pageSize: 50,
      phase: 1,
      round: GameStatus.value.chessRound,
      revocableOnly: true
    }),
    onSuccess: (resp) => {
      teamLogs.value = resp.data as ApiMap['/log/team/list']['resp']
    }
  })
}

// ---------- 优秀者奖励：暂存 / 修改 / 删除 / 发放 ----------
const openRewardModal = () => {
  rewardObject.data = {
    teamId: 0,
    stepCount: 1,
    reason: ''
  }
  rewardObject.showOverlay = true
}

const useSubmitReward = (data: unknown) => {
  if (!GameStatus.value) return
  const d = data as { teamId: number; stepCount: number; reason: string }
  if (!d.teamId) return
  useApi({
    api: game.TempReward({
      gameId: GameStatus.value.id,
      teamId: Number(d.teamId),
      stepCount: Number(d.stepCount),
      reason: d.reason ?? ''
    }),
    onSuccess: () => {
      rewardObject.showOverlay = false
      loadRewardQueue()
      emits('update')
      activeTab.value = 'todo'
    },
    tip: '奖励已暂存，已追加到对应组的待办事项尾部'
  })
}

const openEditRewardModal = (
  reward: ApiMap['/game/reward/queue']['resp']['rewards'][0]
) => {
  editingRewardId.value = reward.id
  // 后端 RewardUpdateReq 只接受 stepCount + reason，不允许换组；只 expose 这两个字段
  editRewardObject.data = {
    stepCount: reward.stepCount,
    reason: reward.reason ?? ''
  }
  editRewardObject.showOverlay = true
}

const useSubmitEditReward = (data: unknown) => {
  if (!editingRewardId.value || editingRewardId.value === '0') return
  const d = data as { stepCount: number; reason: string }
  useApi({
    api: game.UpdateReward({
      id: editingRewardId.value,
      stepCount: Number(d.stepCount),
      reason: d.reason ?? ''
    }),
    onSuccess: () => {
      editRewardObject.showOverlay = false
      editingRewardId.value = 0
      loadRewardQueue()
      emits('update')
    },
    tip: '奖励修改成功'
  })
}

// ---------- 发放单条奖励：改为让用户在棋盘上点选格子 ----------
const openIssueRewardModal = (
  reward: ApiMap['/game/reward/queue']['resp']['rewards'][0]
) => {
  emits('startIssueReward', {
    rewardId: reward.id,
    teamId: reward.teamId,
    stepCount: reward.stepCount
  })
}

const loadRewardQueue = () => {
  if (!GameStatus.value) return
  useApi({
    api: game.GetRewardQueue(GameStatus.value.id),
    onSuccess: (resp) => {
      rewardQueue.value = resp.data as ApiMap['/game/reward/queue']['resp']
    }
  })
}

const deleteRewardItem = (id: number) => {
  useApi({
    api: game.DeleteReward(id),
    onSuccess: () => {
      loadRewardQueue()
      emits('update')
    },
    tip: '奖励已删除'
  })
}

</script>
